import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { customerApi, setCustomerAuthToken } from "../lib/customerApi";
import { getGuestCart, getGuestWishlist, clearGuestCart, clearGuestWishlist } from "../lib/guestStorage";

const STORAGE_KEY = "sukaanyas_customer_token";
const CustomerAuthContext = createContext({
    user: null,
    loading: true,
    signup: async () => {},
    login: async () => {},
    logout: async () => {},
    refresh: async () => {},
    onPostLogin: async () => {},
});

export function CustomerAuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const restore = useCallback(async () => {
        // CRITICAL: skip /auth/me if returning from OAuth callback so AuthCallback can complete.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) setCustomerAuthToken(stored);
        try {
            const { data } = await customerApi.get("/auth/me");
            setUser(data);
        } catch (_e) {
            if (stored) {
                localStorage.removeItem(STORAGE_KEY);
                setCustomerAuthToken(null);
            }
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        restore();
    }, [restore]);

    const onPostLogin = useCallback(async () => {
        // Merge guest cart and wishlist into the now-authenticated account.
        const guestCart = getGuestCart();
        const guestWish = getGuestWishlist();
        try {
            if (guestCart.length) {
                await customerApi.post("/cart/merge", {
                    items: guestCart.map((i) => ({ productId: i.productId, size: i.size || null, qty: i.qty || 1 })),
                });
                clearGuestCart();
            }
            if (guestWish.length) {
                await customerApi.post("/wishlist/merge", { productIds: guestWish });
                clearGuestWishlist();
            }
        } catch (_e) { /* swallow — ux must continue */ }
    }, []);

    const signup = useCallback(async (email, password, name, extras = {}) => {
        const payload = { email, password, name, ...extras };
        const { data } = await customerApi.post("/auth/signup", payload);
        localStorage.setItem(STORAGE_KEY, data.access_token);
        setCustomerAuthToken(data.access_token);
        setUser(data.user);
        await onPostLogin();
        return data.user;
    }, [onPostLogin]);

    const login = useCallback(async (email, password) => {
        const { data } = await customerApi.post("/auth/login", { email, password });
        localStorage.setItem(STORAGE_KEY, data.access_token);
        setCustomerAuthToken(data.access_token);
        setUser(data.user);
        await onPostLogin();
        return data.user;
    }, [onPostLogin]);

    const logout = useCallback(async () => {
        try {
            await customerApi.post("/auth/logout", {});
        } catch (_e) { /* ignore */ }
        localStorage.removeItem(STORAGE_KEY);
        setCustomerAuthToken(null);
        setUser(null);
    }, []);

    const refresh = useCallback(async () => {
        try {
            const { data } = await customerApi.get("/auth/me");
            setUser(data);
        } catch (_e) {
            setUser(null);
        }
    }, []);

    return (
        <CustomerAuthContext.Provider value={{ user, loading, signup, login, logout, refresh, onPostLogin }}>
            {children}
        </CustomerAuthContext.Provider>
    );
}

export function useCustomerAuth() {
    return useContext(CustomerAuthContext);
}
